import { Copy, Check } from 'lucide-react'
import React, { useRef, useEffect, useState, useCallback } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

export interface NodeTextareaProps {
  value: string
  onChange: (value: string) => void
  onBlur?: (value: string) => void
  placeholder?: string
  maxHeight?: string
  className?: string
  showCopy?: boolean
  copyPosition?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  disabled?: boolean
  autoResize?: boolean
  'data-testid'?: string
}

/**
 * NodeTextarea - A reusable textarea component for collaborative editing
 * 
 * Features:
 * - Auto-resize based on content
 * - Optional copy button with feedback
 * - Proper collaborative state handling (follows syncNodeData pattern)
 * - Accessible and keyboard friendly
 * - Consistent styling with the design system
 */
export const NodeTextarea: React.FC<NodeTextareaProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = 'Enter your content here...',
  maxHeight = '400px',
  className,
  showCopy = true,
  copyPosition = 'top-right',
  disabled = false,
  autoResize = true,
  'data-testid': testId
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [copyFeedback, setCopyFeedback] = useState<string>('')
  const [isFocused, setIsFocused] = useState(false)

  // Auto-resize functionality
  const adjustHeight = useCallback(() => {
    if (!autoResize || !textareaRef.current) return
    
    const textarea = textareaRef.current
    textarea.style.height = 'auto'
    
    const maxHeightPx = parseInt(maxHeight.replace('px', ''))
    const newHeight = Math.min(textarea.scrollHeight, maxHeightPx)
    
    textarea.style.height = `${newHeight}px`
    textarea.style.overflowY = textarea.scrollHeight > maxHeightPx ? 'auto' : 'hidden'
  }, [autoResize, maxHeight])

  // Adjust height when value changes
  useEffect(() => {
    adjustHeight()
  }, [value, adjustHeight])

  // Handle input changes (local state only)
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value)
  }, [onChange])

  // Handle blur (collaborative sync point)
  const handleBlur = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false)
    onBlur?.(e.target.value)
  }, [onBlur])

  const handleFocus = useCallback(() => {
    setIsFocused(true)
  }, [])

  // Copy to clipboard functionality
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopyFeedback('Copied!')
      toast.success('Content copied to clipboard')
      
      // Clear feedback after 2 seconds
      setTimeout(() => setCopyFeedback(''), 2000)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
      setCopyFeedback('Failed')
      toast.error('Failed to copy to clipboard')
      setTimeout(() => setCopyFeedback(''), 2000)
    }
  }, [value])

  // Calculate copy button position styles
  const getCopyButtonStyles = useCallback(() => {
    const baseStyles = {
      position: 'absolute' as const,
      zIndex: 10,
      height: 28,
      width: 28,
      padding: 4,
    }

    switch (copyPosition) {
      case 'top-left':
        return { ...baseStyles, top: 8, left: 8 }
      case 'bottom-right':
        return { ...baseStyles, bottom: 8, right: 8 }
      case 'bottom-left':
        return { ...baseStyles, bottom: 8, left: 8 }
      default: // top-right
        return { ...baseStyles, top: 8, right: 8 }
    }
  }, [copyPosition])

  return (
    <div className={cn('relative w-full', className)}>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
        className={cn(
          'min-h-[80px] resize-none transition-all duration-200',
          autoResize && 'overflow-hidden',
          showCopy && 'pr-10', // Add padding for copy button
          isFocused && 'ring-2 ring-blue-500 ring-opacity-50'
        )}
        style={{
          maxHeight: autoResize ? maxHeight : undefined,
        }}
      />
      
      {showCopy && value && (
        <Button
          variant="outline"
          size="icon"
          onClick={handleCopy}
          disabled={disabled || !value.trim()}
          style={getCopyButtonStyles()}
          title="Copy to clipboard"
          className={cn(
            'transition-opacity duration-200',
            !isFocused && !copyFeedback && 'opacity-60 hover:opacity-100'
          )}
        >
          {copyFeedback ? (
            copyFeedback === 'Copied!' ? (
              <Check className="h-3 w-3 text-green-600" />
            ) : (
              <span className="text-xs text-red-600">!</span>
            )
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </Button>
      )}
    </div>
  )
}

export default NodeTextarea
